import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DataTable, ExportCsvButton, FilterBar, Section, SelectFilter, StatCard } from "@/components/admin/admin-kit";
import { admSupportInbox, admUpdateSupportMessage } from "@/lib/admin-pro.functions";
import { privatePageMetadata } from "@/lib/seo";

export const Route = createFileRoute("/nc/live/adm/support")({
  head: () => privatePageMetadata("/nc/live/adm/support", "Support | Control room", "Internal support inbox."),
  component: AdminSupport,
});

const STATUSES = [
  { value: "all", label: "All" },
  { value: "new", label: "New" },
  { value: "open", label: "Open" },
  { value: "resolved", label: "Resolved" },
  { value: "spam", label: "Spam" },
] as const;

function AdminSupport() {
  const run = useServerFn(admSupportInbox);
  const update = useServerFn(admUpdateSupportMessage);
  const qc = useQueryClient();
  const [status, setStatus] = useState<string>("all");

  const { data, isLoading } = useQuery({ queryKey: ["adm-support"], queryFn: () => run({ data: undefined }) });

  const mut = useMutation({
    mutationFn: (input: { messageId: string; status: "new" | "open" | "resolved" | "spam" }) =>
      update({ data: input }),
    onSuccess: () => {
      toast.success("Message updated");
      void qc.invalidateQueries({ queryKey: ["adm-support"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const contact = (data?.contact ?? []).filter((m) => status === "all" || String(m.status ?? "new") === status);

  return (
    <div className="space-y-6">
      <div className="grid gap-3 sm:grid-cols-4">
        <StatCard label="Contact messages" value={data?.contact.length ?? "—"} tone="hot" />
        <StatCard
          label="Unhandled"
          value={(data?.contact ?? []).filter((m) => (m.status ?? "new") === "new").length}
          tone="warn"
        />
        <StatCard label="Waitlist signups" value={data?.waitlist.length ?? "—"} />
        <StatCard label="Emails sent (recent)" value={data?.emails.length ?? "—"} tone="ok" />
      </div>

      <Section
        title="Contact inbox"
        description="Messages from the public contact form."
        actions={<ExportCsvButton rows={contact as unknown as Record<string, unknown>[]} filename="behot-support" />}
      >
        <FilterBar>
          <SelectFilter label="Status" value={status} onChange={setStatus} options={STATUSES} />
        </FilterBar>
        <DataTable
          rows={contact}
          rowKey={(r) => String(r.id)}
          empty={isLoading ? "Loading…" : "Inbox is clear."}
          columns={[
            {
              key: "created_at",
              header: "When",
              cell: (r) => new Date(String(r.created_at)).toLocaleString(),
              sortValue: (r) => String(r.created_at),
            },
            {
              key: "from",
              header: "From",
              cell: (r) => (
                <div>
                  <p className="font-medium">{String(r.name ?? "—")}</p>
                  <p className="text-xs text-muted-foreground">{String(r.email ?? "")}</p>
                </div>
              ),
            },
            {
              key: "message",
              header: "Message",
              className: "max-w-[420px]",
              cell: (r) => (
                <div>
                  <p className="text-sm font-medium">{String(r.topic ?? "")}</p>
                  <p className="whitespace-pre-wrap text-xs text-muted-foreground">{String(r.message ?? "")}</p>
                </div>
              ),
            },
            {
              key: "status",
              header: "Status",
              cell: (r) => (
                <div className="space-y-2">
                  <Badge variant="outline">{String(r.status ?? "new")}</Badge>
                  <div className="flex flex-wrap gap-1">
                    {(["open", "resolved", "spam"] as const).map((s) => (
                      <Button
                        key={s}
                        size="sm"
                        variant={s === "resolved" ? "hot" : "outline"}
                        disabled={mut.isPending}
                        onClick={() => mut.mutate({ messageId: String(r.id), status: s })}
                      >
                        {s}
                      </Button>
                    ))}
                  </div>
                </div>
              ),
            },
          ]}
        />
      </Section>

      <Section title="Waitlist signups" description="Coming-soon interest captured across the site.">
        <DataTable
          rows={data?.waitlist ?? []}
          rowKey={(r) => String(r.id)}
          empty="No signups yet."
          columns={[
            {
              key: "created_at",
              header: "When",
              cell: (r) => new Date(String(r.created_at)).toLocaleString(),
              sortValue: (r) => String(r.created_at),
            },
            { key: "email", header: "Email", cell: (r) => String(r.email ?? "—") },
            { key: "source", header: "Interest", cell: (r) => String(r.product ?? "—") },
          ]}
        />
      </Section>

      <div className="grid gap-4 lg:grid-cols-2">
        <Section title="Recent emails" description="Transactional and notification email queue.">
          <DataTable
            rows={data?.emails ?? []}
            rowKey={(r) => String(r.id)}
            empty="No emails yet."
            columns={[
              {
                key: "created_at",
                header: "When",
                cell: (r) => new Date(String(r.created_at)).toLocaleString(),
                sortValue: (r) => String(r.created_at),
              },
              { key: "kind", header: "Type", cell: (r) => String(r.kind) },
              { key: "to", header: "To", cell: (r) => String(r.profile?.display_name ?? r.user_id ?? "—") },
              { key: "reference", header: "Reference", cell: (r) => <Badge variant="outline">{String(r.reference)}</Badge> },
            ]}
          />
        </Section>
        <Section title="Recent in-app notifications">
          <DataTable
            rows={data?.notifications ?? []}
            rowKey={(r) => r.id}
            empty="No notifications yet."
            columns={[
              {
                key: "created_at",
                header: "When",
                cell: (r) => new Date(r.created_at).toLocaleString(),
                sortValue: (r) => r.created_at,
              },
              {
                key: "user",
                header: "User",
                cell: (r) => r.profile?.display_name ?? r.user_id.slice(0, 8),
              },
              {
                key: "title",
                header: "Notification",
                cell: (r) => (
                  <div>
                    <p className="text-sm">{r.title}</p>
                    <p className="text-xs text-muted-foreground">{r.type}</p>
                  </div>
                ),
              },
              { key: "read", header: "Read", cell: (r) => (r.read_at ? "Yes" : "No") },
            ]}
          />
        </Section>
      </div>
    </div>
  );
}
